/**
 * Design Constants — the controlled register (KAPL Design Constants Master Rev 0.3).
 *
 * Every constant with its register ID, status, allowed stage, release severity
 * and maker → checker → approver state. A value change needs a reason, makes a
 * new revision and goes back to PENDING; the check and the approval must be
 * other people. Whether each constant may stand in a firm / AFC output is said
 * per row, with the reason — nothing reads as cleared by default.
 *
 * The master's other sheets — release gates, libraries, EU GMP limits, copper
 * sizes, references — are read-only tabs.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ParameterRangesAdmin } from "./RangeField";
import { StructuredValue, KeyValueEditor, parseKeyValues, summarize, isTable } from "./StructuredValue";

const C = { blue: "#1F4E79", green: "#047857", amber: "#B45309", red: "#B91C1C",
            grey: "#6B7280", ink: "#111827", line: "#E5E7EB" };
const STATUS = {
  VERIFIED: ["#ECFDF5", "#047857"], WORKING: ["#EFF6FF", "#1D4ED8"],
  CONFIRM: ["#FFFBEB", "#B45309"], BUDGETARY: ["#FEF3C7", "#92400E"],
  "DATA REQUIRED": ["#FEF2F2", "#B91C1C"], SUPERSEDED: ["#F3F4F6", "#6B7280"],
};
const APPROVAL = { PENDING: ["#F3F4F6", "#4B5563"], CHECKED: ["#EFF6FF", "#1D4ED8"],
                   APPROVED: ["#ECFDF5", "#047857"] };
const th = { padding: "7px 8px", fontSize: 11, fontWeight: 700, color: C.grey,
             borderBottom: `1px solid ${C.line}`, whiteSpace: "nowrap", background: "#fff",
             position: "sticky", top: 0, zIndex: 1 };
const td = { padding: "6px 8px", fontSize: 12.5, borderBottom: "1px solid #F3F4F6", verticalAlign: "top" };
const btn = (bg, fg = "#fff") => ({ padding: "4px 10px", fontSize: 11.5, fontWeight: 600, borderRadius: 6,
                                    border: bg === "#fff" ? `1px solid ${C.line}` : 0, background: bg,
                                    color: fg, cursor: "pointer", whiteSpace: "nowrap" });

function Pill({ map, value, label }) {
  const [bg, fg] = map[value] || ["#F3F4F6", "#4B5563"];
  return <span style={{ background: bg, color: fg, borderRadius: 999, padding: "2px 8px",
                        fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>{label || value || "—"}</span>;
}

function Table({ rows, cols }) {
  if (!rows?.length) return <div style={{ color: C.grey, fontSize: 13 }}>Nothing in this sheet.</div>;
  return (
    <div style={{ overflowX: "auto", border: `1px solid ${C.line}`, borderRadius: 8 }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead><tr>{cols.map(c => <th key={c} style={th}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{cols.map(c => (
              <td key={c} style={td}>
                {c === "Severity" ? <Pill map={{ HOLD: ["#FFFBEB", C.amber], BLOCK: ["#FEF2F2", C.red] }} value={r[c]} />
                  : c === "URL or location" && String(r[c] || "").startsWith("http")
                    ? <a href={r[c]} target="_blank" rel="noreferrer">{r[c]}</a>
                    : (r[c] ?? "—")}
              </td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DesignConstantsRegister({ API, authFetch }) {
  const [data, setData] = useState(null);
  const [ref, setRef] = useState(null);
  const [tab, setTab] = useState("register");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [q, setQ] = useState("");
  const [engine, setEngine] = useState("");
  const [status, setStatus] = useState("");
  const [approval, setApproval] = useState("");
  const [editing, setEditing] = useState(null);     // {key, value, reason}
  const [hist, setHist] = useState({});             // key -> revisions
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/api/eng/constants`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not load the constants");
      setData(d);
    } catch (e) { setErr(String(e.message || e)); }
  }, [API, authFetch]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab !== "register" && !ref) {
      authFetch(`${API}/api/eng/constants/reference`).then(r => r.json()).then(setRef).catch(() => { });
    }
  }, [tab, ref, API, authFetch]);

  const reg = useMemo(() => (data?.rows || []).filter(r => r.constant_id), [data]);
  // The few engine constants the Rev 0.3 register does not cover. Edited here
  // too — this screen is the only place constants are changed.
  const outside = useMemo(() => (data?.rows || []).filter(r => !r.constant_id), [data]);
  const [outEdit, setOutEdit] = useState({});        // key -> value being typed (a table: class -> value)
  const saveOutside = (key, value) => {
    const reason = window.prompt("Why is this value changing?") || "";
    if (!reason.trim()) return;
    call(`${API}/api/eng/constants/${key}`,
      { method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value, reason }) }, `${key} saved.`).then(() => setOutEdit({}));
  };
  const engines = useMemo(() => [...new Set(reg.map(r => r.engine).filter(Boolean))].sort(), [reg]);
  const shown = reg.filter(r =>
    (!engine || r.engine === engine) && (!status || r.status === status)
    && (!approval || (r.approval_state || "PENDING") === approval)
    && (!q || `${r.constant_id} ${r.parameter} ${r.group} ${r.engineering_note}`.toLowerCase()
      .includes(q.toLowerCase())));

  const call = async (url, opts, done) => {
    setErr(""); setMsg("");
    try {
      const r = await authFetch(url, opts);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "The server refused that");
      setMsg(done); setHist({}); await load();
    } catch (e) { setErr(String(e.message || e)); }
  };
  const save = () => {
    let v;
    if (editing.table) {
      const p = parseKeyValues(editing.value);
      if (p.error) { setErr(p.error); return; }
      v = p.value;
    } else {
      v = Number(editing.value);
      if (!Number.isFinite(v)) { setErr("The value has to be a number."); return; }
    }
    call(`${API}/api/eng/constants/${editing.key}`,
      { method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value: v, reason: editing.reason }) },
      `${editing.key} saved as a new revision — it now needs a check and an approval.`)
      .then(() => setEditing(null));
  };
  const signoff = (r, step) => call(`${API}/api/eng/constants/${r.key}/${step}`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
    `${r.constant_id} ${step === "check" ? "checked" : "approved"}.`);
  const showHistory = async (key) => {
    if (open === key) { setOpen(null); return; }
    setOpen(key);
    if (!hist[key]) {
      const r = await authFetch(`${API}/api/eng/constants/${key}/history`);
      const d = await r.json().catch(() => ({}));
      setHist(h => ({ ...h, [key]: d.revisions || [] }));
    }
  };

  const R = data?.register || {};
  const canEdit = data?.can_edit;
  const me = data?.me;
  const tabs = [["register", `Constants (${reg.length})`],
                ["ranges", "Parameter ranges"], ["gates", "Release gates"], ["libraries", "Libraries"], ["gmp", "EU GMP limits"],
                ["copper", "Copper sizes"], ["refs", "References"]];

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", marginBottom: 6 }}>
        <div style={{ fontSize: 13, color: C.ink }}>
          <b>{R.document || "Design Constants Master"}</b> · {R.revision} ·{" "}
          <span style={{ color: C.amber, fontWeight: 600 }}>{R.state}</span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {Object.entries(R.by_status || {}).map(([k, n]) => (
          <span key={k} onClick={() => { setTab("register"); setStatus(status === k ? "" : k); }}
            style={{ cursor: "pointer", opacity: !status || status === k ? 1 : 0.45 }}>
            <Pill map={STATUS} value={k} label={`${k} · ${n}`} />
          </span>
        ))}
        <span style={{ fontSize: 12, color: C.grey, alignSelf: "center" }}>
          · approval: {Object.entries(R.by_approval || {}).map(([k, n]) => `${n} ${k.toLowerCase()}`).join(", ")}
          {" "}· <b style={{ color: R.firm_allowed ? C.green : C.red }}>{R.firm_allowed || 0}</b> of {R.count} cleared for firm / AFC use
        </span>
      </div>
      {(R.mismatches || []).length > 0 && (
        <div style={{ fontSize: 12.5, color: C.amber, background: "#FFFBEB", border: "1px solid #FDE68A",
                      borderRadius: 8, padding: "7px 11px", marginBottom: 10 }}>
          The value in use differs from the register for {R.mismatches.join(", ")} — shown per row. Nothing was
          changed automatically; edit the value (with a reason) or keep it and record why.
        </div>
      )}

      <div style={{ display: "flex", gap: 6, borderBottom: `1px solid ${C.line}`, marginBottom: 12, flexWrap: "wrap" }}>
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: "7px 12px", border: 0, borderBottom: tab === k ? `2px solid ${C.blue}` : "2px solid transparent",
                     background: "none", color: tab === k ? C.blue : C.grey, fontWeight: 600, fontSize: 13,
                     cursor: "pointer" }}>{l}</button>
        ))}
      </div>
      {err && <div style={{ color: C.red, fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      {msg && <div style={{ color: C.green, fontSize: 12.5, marginBottom: 8 }}>{msg}</div>}

      {tab === "register" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search ID, parameter, note…"
              style={{ padding: "6px 10px", border: `1px solid ${C.line}`, borderRadius: 7, fontSize: 13, minWidth: 240 }} />
            {[[engine, setEngine, engines, "All engines"],
              [status, setStatus, Object.keys(STATUS), "All statuses"],
              [approval, setApproval, Object.keys(APPROVAL), "All approval states"]].map(([v, set, opts, all], i) => (
              <select key={i} value={v} onChange={e => set(e.target.value)}
                style={{ padding: "6px 8px", border: `1px solid ${C.line}`, borderRadius: 7, fontSize: 13 }}>
                <option value="">{all}</option>
                {opts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            <span style={{ fontSize: 12, color: C.grey, alignSelf: "center" }}>{shown.length} shown</span>
          </div>
          <div style={{ overflow: "auto", maxHeight: "68vh", border: `1px solid ${C.line}`, borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>{["ID", "Parameter", "Value", "Status", "Stage", "Release", "Approval", "Firm / AFC use", ""]
                  .map(h => <th key={h} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {shown.map(r => {
                  const lookup = r.kind === "lookup";
                  const edit = editing && editing.key === r.key;
                  const state = r.approval_state || "PENDING";
                  return (
                    <React.Fragment key={r.key}>
                      <tr>
                        <td style={{ ...td, fontFamily: "monospace", fontSize: 12, whiteSpace: "nowrap" }}>
                          {r.constant_id}<div style={{ color: C.grey, fontFamily: "inherit" }}>{r.revision}</div>
                        </td>
                        <td style={{ ...td, minWidth: 220 }}>
                          <b style={{ fontWeight: 600 }}>{r.parameter}</b>
                          <div style={{ color: C.grey, fontSize: 11.5 }}>{r.engine} · {r.group}{r.scope ? ` · ${r.scope}` : ""}</div>
                          {r.engineering_note && <div style={{ color: C.grey, fontSize: 11.5, marginTop: 2 }}>{r.engineering_note}</div>}
                        </td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {edit ? (
                            <div style={{ display: "grid", gap: 4, minWidth: 200 }}>
                              {editing.table ? (
                                <KeyValueEditor value={editing.value} base={r.raw} baseLabel="Saved" unit={r.unit}
                                  onChange={v => setEditing(s => ({ ...s, value: v }))} />
                              ) : (
                                <input autoFocus type="number" step="any" value={editing.value}
                                  onChange={e => setEditing(s => ({ ...s, value: e.target.value }))}
                                  style={{ padding: "4px 6px", border: `1px solid ${C.blue}`, borderRadius: 6 }} />
                              )}
                              <input value={editing.reason} placeholder="Why it is changing (required)"
                                onChange={e => setEditing(s => ({ ...s, reason: e.target.value }))}
                                style={{ padding: "4px 6px", border: `1px solid ${C.line}`, borderRadius: 6 }} />
                              <div style={{ display: "flex", gap: 4 }}>
                                <button style={btn(C.blue)} disabled={!editing.reason.trim()} onClick={save}>Save revision</button>
                                <button style={btn("#fff", C.grey)} onClick={() => setEditing(null)}>Cancel</button>
                              </div>
                            </div>
                          ) : lookup ? (
                            <span style={{ color: C.red, fontSize: 12 }}>no value — {r.unit}</span>
                          ) : (
                            <>
                              {isTable(r.raw) ? <StructuredValue value={r.raw} unit={r.unit} />
                                : <><b>{String(r.raw)}</b> <span style={{ color: C.grey }}>{r.unit}</span></>}
                              {r.matches_register === false && (
                                <div style={{ color: C.amber, fontSize: 11.5 }}>register: {summarize(r.register_value)}</div>
                              )}
                              {(r.min != null || r.max != null) && (
                                <div style={{ color: C.grey, fontSize: 11 }}>range {r.min ?? "—"} … {r.max ?? "—"}</div>
                              )}
                            </>
                          )}
                        </td>
                        <td style={td}><Pill map={STATUS} value={r.status} /></td>
                        <td style={{ ...td, fontSize: 12 }}>{r.allowed_stage}</td>
                        <td style={td}><Pill map={{ HOLD: ["#FFFBEB", C.amber], BLOCK: ["#FEF2F2", C.red] }} value={r.release_severity} /></td>
                        <td style={{ ...td, fontSize: 11.5, whiteSpace: "nowrap" }}>
                          <Pill map={APPROVAL} value={state} />
                          <div style={{ color: C.grey, marginTop: 3 }}>maker {r.maker || "—"}</div>
                          {r.checked_by && <div style={{ color: C.grey }}>checked {r.checked_by}</div>}
                          {r.approver && <div style={{ color: C.grey }}>approved {r.approver}</div>}
                        </td>
                        <td style={{ ...td, fontSize: 12, color: r.firm_use?.allowed ? C.green : C.red, minWidth: 150 }}>
                          {r.firm_use?.allowed ? "✓ allowed" : `✗ ${r.firm_use?.reason}`}
                        </td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {canEdit && !lookup && r.override_policy !== "NO" && !edit && (
                              <button style={btn("#fff", C.blue)}
                                onClick={() => setEditing({ key: r.key, reason: "", table: isTable(r.raw),
                                                    value: isTable(r.raw) ? { ...r.raw } : String(r.raw ?? "") })}>Edit</button>
                            )}
                            {canEdit && state === "PENDING" && r.maker !== me && (
                              <button style={btn(C.blue)} onClick={() => signoff(r, "check")}>Check</button>
                            )}
                            {canEdit && state === "CHECKED" && r.maker !== me && r.checked_by !== me && (
                              <button style={btn(C.green)} onClick={() => signoff(r, "approve")}>Approve</button>
                            )}
                            <button style={btn("#fff", C.grey)} onClick={() => showHistory(r.key)}>
                              {open === r.key ? "Hide" : "History"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {open === r.key && (
                        <tr><td colSpan={9} style={{ ...td, background: "#F9FAFB" }}>
                          <div style={{ fontSize: 12, color: C.grey, marginBottom: 4 }}>
                            Source: {r.source || "—"}{r.dependencies ? ` · depends on ${r.dependencies}` : ""}
                          </div>
                          {(hist[r.key] || []).map((h, i) => (
                            <div key={i} style={{ fontSize: 12, padding: "2px 0" }}>
                              <b>{h.revision}</b> · {h.action.replace("_", " ").toLowerCase()} · value {summarize(h.value)}
                              {" "}· {h.by} · {h.at}{h.reason ? ` — ${h.reason}` : ""}
                            </div>
                          ))}
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {outside.length > 0 && (
            <div style={{ marginTop: 16, border: `1px solid ${C.line}`, borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4 }}>Outside the Rev 0.3 register</div>
              <div style={{ fontSize: 12, color: C.grey, marginBottom: 10 }}>
                Engine values the master does not list. They carry no register ID and are not cleared for
                firm use until KAPL adds them to the master.
              </div>
              {outside.map(r => r.kind === "json" && isTable(r.raw) ? (
                <div key={r.key} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>
                    {r.parameter} ({r.unit}) <span style={{ color: C.grey, fontWeight: 400 }}>· {summarize(r.raw)}</span>
                  </div>
                  {canEdit ? (() => {
                    // ISO rows are held in the register as DC-CLASS-00x and GRADE
                    // rows follow their ISO class, so only the rest is edited here.
                    const locked = Object.fromEntries(Object.keys(r.raw).map(cls => [cls,
                      /^ISO \d/.test(cls) ? "register row (DC-CLASS)" : /^GRADE /.test(cls) ? "follows its ISO class" : ""])
                      .filter(([, why]) => why));
                    const draft = outEdit[r.key] || r.raw;
                    const dirty = Object.keys(draft).some(k => String(draft[k]) !== String(r.raw[k]));
                    return (
                      <>
                        <KeyValueEditor value={draft} base={r.raw} baseLabel="Saved" unit={r.unit} locked={locked}
                          onChange={v => setOutEdit(s => ({ ...s, [r.key]: v }))} />
                        {dirty && (
                          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                            <button style={btn(C.blue)} onClick={() => {
                              const p = parseKeyValues(draft);
                              if (p.error) { setErr(p.error); return; }
                              saveOutside(r.key, p.value);
                            }}>Save</button>
                            <button style={btn("#fff", C.grey)}
                              onClick={() => setOutEdit(s => { const n = { ...s }; delete n[r.key]; return n; })}>Cancel</button>
                          </div>
                        )}
                      </>
                    );
                  })() : <StructuredValue value={r.raw} unit={r.unit} open />}
                </div>
              ) : (
                <div key={r.key} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5, marginBottom: 6 }}>
                  <span style={{ minWidth: 280 }}>{r.parameter}</span>
                  {canEdit ? (
                    <>
                      <input type="number" step="any" value={outEdit[r.key] ?? r.raw} style={{ width: 90 }}
                        onChange={e => setOutEdit(s => ({ ...s, [r.key]: e.target.value }))} />
                      <span style={{ color: C.grey }}>{r.unit}</span>
                      {outEdit[r.key] !== undefined && Number(outEdit[r.key]) !== Number(r.raw) && (
                        <button style={btn(C.blue)} onClick={() => saveOutside(r.key, Number(outEdit[r.key]))}>Save</button>
                      )}
                    </>
                  ) : <b>{String(r.raw)} <span style={{ color: C.grey, fontWeight: 400 }}>{r.unit}</span></b>}
                  <span style={{ color: C.grey, fontSize: 11.5 }}>{r.source}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: C.grey, marginTop: 8 }}>
            A change needs a reason, becomes a new revision and returns to PENDING. The maker cannot check it,
            and neither the maker nor the checker can approve it. Firm / AFC outputs need an APPROVED record
            that is not budget-stage, CONFIRM, BUDGETARY or DATA REQUIRED.
          </div>
        </>
      )}

      {tab === "ranges" && <ParameterRangesAdmin api={API} authFetch={authFetch} />}
      {tab === "gates" && <Table rows={ref?.release_gates} cols={["Gate ID", "Discipline", "Required evidence", "Failure example", "Severity"]} />}
      {tab === "libraries" && <Table rows={ref?.libraries} cols={["Library ID", "Engine", "Key dimensions", "Source basis", "Required data", "Final-use rule"]} />}
      {tab === "gmp" && <Table rows={ref?.gmp_limits} cols={["Grade", "State", "Particle size", "Classification Table 1", "Monitoring Table 5", "Note"]} />}
      {tab === "copper" && <Table rows={ref?.copper_sizes} cols={["Nominal OD (inch)", "OD (mm)", "Wall (mm)", "Temper", "Pressure rating", "Approved SKU", "Selection status"]} />}
      {tab === "refs" && (
        <>
          <Table rows={ref?.references} cols={["Source ID", "Title / edition", "URL or location", "Use and limitation"]} />
          <div style={{ marginTop: 14 }}>
            <Table rows={ref?.resolution} cols={["Resolution step", "Developer rule"]} />
          </div>
        </>
      )}
    </div>
  );
}
